import {
    type OperationVariables,
    type TypedDocumentNode,
    isNetworkRequestSettled,
    useQuery,
} from '@apollo/client';
import { useCallback, useRef, useState } from 'react';

type UseRefetchQueriesResult = {
    refetch: () => Promise<void>;
    isComplete: boolean;
};

type ExtractVariables<T> =
    T extends TypedDocumentNode<any, infer V> ? V : never;

type RefetchQueryEntry<TQuery extends TypedDocumentNode<any, any>> = {
    query: TQuery;
    variables: ExtractVariables<TQuery>;
};

// Comments copied for historical context:
/*
 * These cannot be executed under the mutation's `refetchQueries` option,
 * because the latter does not allow you to specify an error policy on the
 * queries to be refetched. In other places across our app where these are
 * called, we use `errorPolicy: 'all'`, but we should not be passing that to
 * the mutation, because there's no concept of a partial success when
 * verifying a patient.
 */

/*
 * Apollo has deprecated all callbacks like `onCompleted` in `useQuery` and
 * `useLazyQuery`, instead recommending that applications track the network
 * status. This effect keeps track of both queries, waiting for them to
 * complete before redirecting the user.
 *
 * See:
 * https://github.com/apollographql/apollo-client/issues/12352
 * https://tkdodo.eu/blog/breaking-react-querys-api-on-purpose
 */

/**
 * Custom hook to batch refetch multiple Apollo queries on demand.
 * This is necessary after calling a mutation, in order to update stored values in the cache.
 *
 * @param queries - An array where each entry is an object with a `query` (GraphQL document) and optional `variables`.
 * @returns refetch, isComplete, isCalled, isLoading
 * Note:
 * - The set of queries is locked on the first render. Changing the `queries` array
 *   after the first render will have no effect.
 * - The hook does not return the query results, only status indicators and a trigger function.
 *
 * Example:
 * Using the hook requires three steps:
 * 1. Define the queries you want to refetch.
 * 2. Call the `refetch` function to trigger the refetch.
 * 3. Use the `isComplete` flag to determine when all queries have finished refetching.
 * ```ts
 * 1. Define the queries:
 * const { refetch, isComplete } = useRefetchQueries([
 *   {
 *     query: GetPrescriberOrderDocument,
 *     variables: { prescriberOrderId },
 *   },
 * ]);
 * }, [isComplete]);
 *
 * 2. Call the `refetch` function when you want to trigger the refetch.
 * const mutationAction = useCallback(async () => {
 *   const result = await mutation({
 *     variables: { prescriberOrderId },
 *   });
 *   if (result.data?.sendPrescriberOrder?.success) {
 *     refetch();
 *   }
 * }, []);
 *
 * 3. Use the `isComplete` flag to determine when all queries have finished refetching.
 * useEffect(() => {
 *   if (isComplete) {
 *      navigate(
 *       getAbsoluteRoute('order.prescriberOrderId.confirmation', {
 *         prescriberOrderId,
 *       })
 *     );
 *   }
 * ```
 */
export function useRefetchQueries<
    TEntries extends readonly RefetchQueryEntry<TypedDocumentNode<any, any>>[],
>(
    queries: TEntries
): UseRefetchQueriesResult & { isCalled: boolean; isLoading: boolean } {
    // Lock in the queries object on the first render
    const queriesRef = useRef(queries);

    const [isComplete, setIsComplete] = useState(false);
    const [isCalled, setIsCalled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Prepare useQuery hooks for each entry (only query and variables)
    const queryResults = Object.entries(queriesRef.current).map(([_, entry]) =>
        // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
        useQuery(entry.query, {
            variables:
                'variables' in entry
                    ? (entry.variables as OperationVariables)
                    : undefined,
            errorPolicy: 'all',
            fetchPolicy: 'network-only',
            skip: true, // We'll trigger manually
            notifyOnNetworkStatusChange: true,
        })
    );

    const refetch = useCallback(async () => {
        setIsCalled(true);
        setIsComplete(false);
        setIsLoading(true);
        await Promise.all(
            queryResults.map(async (result) => {
                if (result.refetch) {
                    await result.refetch();
                }
            })
        );
        setIsLoading(false);
        setIsComplete(true);
    }, [queryResults]);

    // isComplete is true if all queries are not loading and not refetching
    const allComplete =
        !isLoading &&
        queryResults.every((result) =>
            isNetworkRequestSettled(result.networkStatus)
        );

    return {
        refetch,
        isComplete: isComplete && allComplete,
        isCalled,
        isLoading,
    };
}
